import React, {
    useEffect, useState, useRef, useCallback
} from 'react';

import { wrapFunctionalComponent } from 'utils/framework';
import isFunction from 'utils/functions/isFunction';

import { Flex } from 'components/ui';
import TextInput from 'components/Inputs/TextInput/TextInput';
import { colors, fontSizes, fontWeights } from 'style/config';
import stringUtils from 'utils/String';

function OtpInputGroup({
    otpLength = 6, onComplete, error = false, disabled = false, clearError, localization
}) {
    const [otpCode, setOtpCode] = useState(Array(otpLength).fill(''));
    const [hasError, setHasError] = useState(false);
    const [focusedIndex, setFocusedIndex] = useState(0);

    const inputRefs = useRef([]);
    const lastSubmittedCode = useRef('');
    const onCompleteRef = useRef(onComplete);
    onCompleteRef.current = onComplete;

    // Distribute a multi-character value (iOS SMS autofill, paste, or a fast-typing burst)
    // across the inputs starting at startIndex, filling left-to-right. Returns true when at
    // least two digits were distributed. This is the path that fixes iOS autofill: the inputs
    // no longer carry maxLength, so WebKit delivers the full code in a single onChange and it
    // is fanned out here instead of only the first digit landing.
    const distributeOtpValue = useCallback(
        (value, startIndex = 0) => {
            const digits = value.replace(/\D/g, '').slice(0, otpLength - startIndex);

            if (digits.length > 1) {
                setHasError(false);
                isFunction(clearError) && clearError();

                // Functional update to avoid a stale closure during rapid autofill, and clear
                // everything from startIndex onward before fanning out so distributed digits
                // replace the remainder -- this prevents a stale digit to the right (e.g. a
                // previous wrong code) from lingering and falsely satisfying onComplete.
                setOtpCode(prev => {
                    const newOtp = prev.slice(0, startIndex).concat(Array(otpLength - startIndex).fill(''));

                    digits.split('').forEach((digit, i) => {
                        newOtp[startIndex + i] = digit;
                    });

                    return newOtp;
                });
                inputRefs.current[Math.min(startIndex + digits.length, otpLength) - 1]?.focus();

                return true;
            }

            return false;
        },
        [otpLength, clearError]
    );

    const handleInputChange = (e, index) => {
        setHasError(false);
        isFunction(clearError) && clearError();

        const rawValue = e.target.value;

        // The box is a controlled single-digit input with no DOM maxLength (removed to let iOS
        // autofill deliver the full code). When a user retypes into a box that already holds a
        // digit, the browser briefly keeps BOTH chars, so onChange fires with a length-2 value
        // ('58' if the caret was after the digit, '85' if before) -- that is a REPLACE, not a
        // distribute. Compute the truly-new content by removing one occurrence of the digit
        // currently stored for this box:
        //   - '58'/'85' with old '5'  -> '8'      (single digit -> replace, caret-order agnostic)
        //   - full '123456' autofill  -> '123456' (old '' removes nothing -> distribute)
        //   - 2-char burst into empty -> both chars survive (old '' -> distribute)
        const oldDigit = otpCode[index];
        let newChars = rawValue;

        if (oldDigit) {
            const removeAt = rawValue.indexOf(oldDigit);

            if (removeAt !== -1) {
                newChars = rawValue.slice(0, removeAt) + rawValue.slice(removeAt + 1);
            }
        }

        // Genuine multi-digit input (autofill/paste/fast typing) is distributed and returns
        // here, so it never falls through to the single-digit branch that would empty the box.
        // Branch on digit count (not raw length) so a value like '1 ' or 'a1' still applies its
        // single digit rather than being swallowed by an early return.
        const digits = newChars.replace(/\D/g, '');

        if (digits.length > 1) {
            distributeOtpValue(newChars, index);

            return;
        }

        // Single digit (fresh fill or a retype-replace of this box) or an explicit clear
        if (digits.length === 1 || newChars === '') {
            const newOtp = [...otpCode];
            newOtp[index] = digits;
            setOtpCode(newOtp);

            // Focus management (e.g., move to next input)
            if (digits && index < otpLength - 1) {
                inputRefs.current[index + 1]?.focus();
            }
        } else {
            inputRefs.current[index].empty();
        }
    };

    const handleKeyDown = (e, index) => {
        // Move focus to the previous input on Backspace if the current input is empty
        if (e.key === 'Backspace' && otpCode[index] === '' && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
    };

    const handlePaste = e => {
        e.preventDefault();

        const pastedText = e.clipboardData.getData('text');
        const pastedTextToOtpLength = pastedText.slice(0, otpLength).split('');
        const isValidPaste = pastedTextToOtpLength.every(char => char.match(/^\d$/));

        if (isValidPaste) {
            setHasError(false);
            isFunction(clearError) && clearError();

            setOtpCode(() => {
                const newOtp = Array(otpLength).fill('');

                for (let i = 0; i < otpLength; i++) {
                    newOtp[i] = pastedTextToOtpLength[i] ?? '';
                }

                return newOtp;
            });

            // Focus on the last filled input or the end
            if (pastedTextToOtpLength.length > 0) {
                inputRefs.current[pastedTextToOtpLength.length - 1]?.focus();
            }
        }
    };

    useEffect(() => {
        if (otpCode.every(digit => digit !== '') && isFunction(onCompleteRef.current)) {
            const code = otpCode.join('');

            if (code !== lastSubmittedCode.current) {
                lastSubmittedCode.current = code;
                onCompleteRef.current(code);
            }
        }
    }, [otpCode]);

    // Clear inputs on error
    useEffect(() => {
        let focusTimer;

        if (error) {
            setHasError(true);
            setOtpCode(Array(otpLength).fill(''));
            lastSubmittedCode.current = '';
            focusTimer = setTimeout(() => inputRefs.current[0]?.focus(), 0);
        } else {
            setHasError(false);
        }

        return () => clearTimeout(focusTimer);
    }, [error, otpLength]);

    // Focus on the first input on mount
    useEffect(() => {
        inputRefs.current[0]?.focus();
    }, []);

    return (
        <Flex
            is='fieldset'
            justifyContent='center'
            gap={4}
            onPaste={handlePaste}
        >
            {otpCode.map((digit, index) => {
                const isActive = digit !== '' || focusedIndex === index;
                const isFirstInput = index === 0;

                return (
                    <TextInput
                        key={index}
                        autoOff={true}
                        autoComplete={isFirstInput ? 'one-time-code' : undefined}
                        inputMode='numeric'
                        pattern='[0-9]*'
                        value={digit}
                        onChange={e => handleInputChange(e, index)}
                        onKeyDown={e => handleKeyDown(e, index)}
                        onFocus={() => setFocusedIndex(index)}
                        onBlur={() => setFocusedIndex(-1)}
                        ref={el => (inputRefs.current[index] = el)}
                        height={44}
                        marginBottom={0}
                        disabled={disabled}
                        aria-label={stringUtils.format(localization.accessibilityOtpInput, index + 1)}
                        customStyle={{
                            input: {
                                textAlign: 'center',
                                paddingLeft: 0,
                                paddingRight: 0,
                                minWidth: 44,
                                fontWeight: fontWeights.bold,
                                fontSize: fontSizes.md,
                                margin: 0,
                                caretColor: colors.black
                            },
                            innerWrap: {
                                maxWidth: 44,
                                borderColor: hasError ? colors.error : isActive ? colors.black : colors.midGray
                            }
                        }}
                    />
                );
            })}
        </Flex>
    );
}

export default wrapFunctionalComponent(OtpInputGroup, 'OtpInputGroup');
